import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { customerLogin, customerRegister } from "@/api";

export function GoogleButton({ redirectPath = "/account" }) {
  const { t } = useApp();
  const onGoogle = () => {
    // Remember where to land after the OAuth round-trip (read in AuthCallback).
    sessionStorage.setItem("hg_post_auth_redirect", redirectPath);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={onGoogle} type="button" data-testid="google-login-btn"
      className="flex items-center justify-center gap-3 w-full border border-ink py-3.5 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-ink hover:text-paper">
      <svg width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M21.35 11.1H12v3.2h5.35c-.25 1.4-1.6 4.1-5.35 4.1-3.22 0-5.85-2.67-5.85-5.95S8.78 6.5 12 6.5c1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.73 3.9 14.6 3 12 3 6.98 3 2.9 7.03 2.9 12s4.08 9 9.1 9c5.25 0 8.73-3.69 8.73-8.89 0-.6-.07-1.05-.15-1.51z"/></svg>
      {t.auth.google}
    </button>
  );
}

export function CustomerAuthForms({ onSuccess, redirectPath = "/account" }) {
  const { loginCustomer, t } = useApp();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = mode === "login"
        ? await customerLogin({ email: form.email, password: form.password })
        : await customerRegister({ email: form.email, password: form.password, name: form.name });
      await loginCustomer(data);
      toast.success(mode === "login" ? t.auth.welcomeBack : t.auth.created);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t.auth.error);
    } finally { setLoading(false); }
  };

  const inputCls = "mt-2 w-full bg-transparent border-b border-line py-2.5 font-light focus:outline-none focus:border-ink transition-colors";

  return (
    <div className="w-full" data-testid="auth-forms">
      <div className="flex gap-6 mb-8 border-b border-line">
        {["login", "register"].map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)} data-testid={`tab-${m}`}
            className={`pb-3 text-xs uppercase tracking-[0.2em] -mb-px border-b-2 transition-colors ${mode === m ? "border-ink text-ink" : "border-transparent text-muted hover:text-ink"}`}>
            {m === "login" ? t.auth.signIn : t.auth.createAccount}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-5">
        {mode === "register" && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{t.auth.name}</label>
            <input value={form.name} onChange={upd("name")} data-testid="reg-name" className={inputCls} placeholder={t.auth.namePh} />
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{t.auth.email}</label>
          <input required type="email" value={form.email} onChange={upd("email")} data-testid="auth-email" className={inputCls} placeholder={t.auth.emailPh} />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{t.auth.password}</label>
          <input required type="password" value={form.password} onChange={upd("password")} data-testid="auth-password" className={inputCls} placeholder={t.auth.passwordPh} />
        </div>
        <button type="submit" disabled={loading} data-testid="auth-submit"
          className="flex items-center justify-center gap-2 w-full bg-ink text-paper py-3.5 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? t.auth.signIn : t.auth.createAccount)}
        </button>
      </form>
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-line" /><span className="text-[10px] uppercase tracking-[0.2em] text-muted">{t.auth.or}</span><div className="flex-1 h-px bg-line" />
      </div>
      <GoogleButton redirectPath={redirectPath} />
    </div>
  );
}
